import type {
  MessengerChatForm,
  MessengerEmailForm,
  MessengerPreChatForm,
  MessengerSmsForm,
} from '@widgets/messenger/types'
import { cloneDeep } from 'lodash-es'
import { useWidgetLocalStorageValue } from '@widgets/messenger/compositions/useWidgetLocalStorageValue'
import { useWidgetStore } from '@widgets/messenger/stores/useWidgetStore'
import { computed } from '@/composition/vue/compositionApi'

type WidgetFormCache = {
  email: Partial<MessengerEmailForm>
  sms: Partial<MessengerSmsForm>
  preChat: Partial<MessengerPreChatForm>
  chat: Partial<MessengerChatForm>
}

export type WidgetFormCacheKey = keyof WidgetFormCache

const formFieldKeys = {
  email: ['name', 'email', 'message'],
  sms: ['name', 'phone', 'country', 'message'],
  preChat: ['name', 'email', 'message'],
  chat: ['message'],
} satisfies {
  [FormKey in WidgetFormCacheKey]: (keyof WidgetFormCache[FormKey])[]
}
const formKeys = Object.keys(formFieldKeys) as WidgetFormCacheKey[]
const synchronizedFieldKeys = ['name', 'email']

export const useWidgetFormCache = () => {
  const { uniqueCode, domain } = useWidgetStore()

  const defaultCacheValue: WidgetFormCache = {
    email: {},
    sms: {},
    preChat: {},
    chat: {},
  }

  const {
    value: cacheValue,
    set: setCache,
    remove: removeCache,
  } = useWidgetLocalStorageValue<Partial<WidgetFormCache>>(
    {
      uniqueCode: uniqueCode.value,
      domain: domain.value,
      dataKey: 'formCache',
    },
    defaultCacheValue,
  )

  const formValues = computed(() => {
    return {
      ...defaultCacheValue,
      ...cacheValue.value,
    }
  })

  const updateFormValues = <FormKey extends WidgetFormCacheKey>(
    formKey: FormKey,
    values: Partial<WidgetFormCache[FormKey]>,
  ) => {
    const newCacheValue = cloneDeep(cacheValue.value)
    newCacheValue[formKey] = values

    Object.keys(values).forEach((fieldKey) => {
      if (!synchronizedFieldKeys.includes(fieldKey)) {
        return
      }
      const otherFormKeys = formKeys.filter((key) => key !== formKey)
      otherFormKeys.forEach((key) => {
        if (formFieldKeys[key].includes(fieldKey as keyof WidgetFormCache[keyof WidgetFormCache])) {
          newCacheValue[key]![fieldKey] = values[fieldKey]
        }
      })
    })

    setCache(newCacheValue)
  }

  const resetField = <FormKey extends WidgetFormCacheKey, FieldKey extends keyof WidgetFormCache[FormKey]>(
    formKey: FormKey,
    fieldKey: FieldKey,
  ) => {
    const newCacheValue = cloneDeep(cacheValue.value)
    const form = newCacheValue[formKey]
    if (form && fieldKey in form) {
      delete newCacheValue[formKey]![fieldKey]
    }

    if (synchronizedFieldKeys.includes(fieldKey as keyof WidgetFormCache[keyof WidgetFormCache])) {
      const otherFormKeys = formKeys.filter((key) => key !== formKey)
      otherFormKeys.forEach((key) => {
        if (
          formFieldKeys[key].includes(fieldKey as keyof WidgetFormCache[keyof WidgetFormCache]) &&
          fieldKey in newCacheValue[key]!
        ) {
          // @ts-expect-error we already checked that fieldKey in newCacheValue[key]
          delete newCacheValue[key]![fieldKey]
        }
      })
    }

    setCache(newCacheValue)
  }

  const resetForm = <FormKey extends WidgetFormCacheKey>(formKey: FormKey) => {
    const newCacheValue = cloneDeep(cacheValue.value)
    newCacheValue[formKey] = {}
    setCache(newCacheValue)
  }

  const resetAll = () => {
    removeCache()
  }

  return {
    formValues,
    updateFormValues,
    resetField,
    resetForm,
    resetAll,
  }
}
